import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRight, Building2, CalendarClock, CheckCircle2, Clock, FileWarning, Flame, GraduationCap, Heart, ListChecks, ShieldCheck, Users } from "lucide-react";
import { apiRequest } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RisksByCategoryChart, RisksByLevelChart, DeadlinesByMonthChart, ActionsStatusChart } from "@/components/DashboardCharts";

function StatCard({ icon: Icon, label, value, tone, testId }) {
  return (
    <Card className="rounded-lg border-slate-200 shadow-none transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-sm" data-testid={testId}>
      <CardContent className="flex items-center justify-between p-5">
        <div><p className="text-sm font-medium text-slate-500" data-testid={`${testId}-label`}>{label}</p><p className="mt-2 font-heading text-3xl font-bold text-slate-950" data-testid={`${testId}-value`}>{value}</p></div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-md ${tone}`}><Icon className="h-5 w-5" /></div>
      </CardContent>
    </Card>
  );
}

function KpiBars({ title, data, colors, testId }) {
  const total = data.reduce((sum, [, v]) => sum + v, 0) || 1;
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
      <div className="mt-3 space-y-2">
        {data.map(([key, val], i) => (
          <div key={key} className="space-y-1" data-testid={`${testId}-${key}`}>
            <div className="flex items-center justify-between text-xs"><span className="capitalize text-slate-700">{key}</span><span className="font-mono font-semibold text-slate-900">{val}</span></div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full ${colors[i % colors.length]} transition-[width] duration-500`} style={{ width: `${(val / total) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiTopList({ title, items, field, suffix = "", tone = "blue", testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
      <ul className="mt-3 space-y-2">
        {(items || []).length === 0 && <li className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">—</li>}
        {(items || []).map((it, idx) => (
          <li key={it.company_id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`${testId}-row-${idx}`}>
            <Link to={`/aziende/${it.company_id}`} className="truncate font-medium text-slate-800 hover:text-blue-700">{it.company_name}</Link>
            <Badge className={tone === "rose" ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-900"}>{it[field]}{suffix}</Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}

const severityClass = {
  overdue: "bg-red-100 text-red-800 border-red-200",
  urgent: "bg-orange-100 text-orange-800 border-orange-200",
  soon: "bg-amber-100 text-amber-800 border-amber-200",
  scheduled: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const severityLabel = { overdue: "Scaduta", urgent: "Urgente", soon: "A breve", scheduled: "Pianificata" };

export default function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [deadlines, setDeadlines] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [consultantKpi, setConsultantKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiRequest("/dashboard/stats"),
      apiRequest("/deadlines"),
      apiRequest("/dashboard/kpi-aggregated").catch(() => null),
      apiRequest("/dashboard/consultant-kpi").catch(() => null),
    ])
      .then(([s, d, k, ck]) => { setStats(s); setDeadlines(d); setKpi(k); setConsultantKpi(ck); })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats || !deadlines) return <div className="h-40 rounded-lg border border-slate-200 bg-white p-6" data-testid="dashboard-loading">{t("common.loading")}</div>;

  const topDeadlines = (deadlines.items || []).slice(0, 6);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dashboard-hero">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
          <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900" data-testid="dashboard-kicker">Control room DVR</p><h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="dashboard-title">Stato compliance aziende</h2><p className="mt-3 max-w-2xl text-slate-600" data-testid="dashboard-description">Monitora completamento DVR, dati obbligatori mancanti e accessi collegati alle aziende clienti.</p></div>
          <Button asChild className="bg-blue-900 hover:bg-blue-800" data-testid="dashboard-new-company-button"><Link to="/aziende">Gestisci aziende <ArrowRight className="h-4 w-4" /></Link></Button>
        </div>
      </section>
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5" data-testid="dashboard-stats-grid">
        <StatCard icon={Building2} label="Aziende" value={stats.companies_total} tone="bg-blue-50 text-blue-900" testId="stat-companies" />
        <StatCard icon={CheckCircle2} label="Completamento medio" value={`${stats.average_completion}%`} tone="bg-emerald-50 text-emerald-700" testId="stat-completion" />
        <StatCard icon={AlertTriangle} label="Criticità" value={stats.critical_warnings} tone="bg-red-50 text-red-700" testId="stat-critical" />
        <StatCard icon={Users} label="Utenti attivi" value={stats.users_total} tone="bg-slate-100 text-slate-700" testId="stat-users" />
        <StatCard icon={Clock} label="Scadenze 30gg" value={stats.upcoming_deadlines} tone="bg-amber-50 text-amber-700" testId="stat-deadlines" />
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-deadlines-grid">
        <StatCard icon={Flame} label="Scadenze totali" value={deadlines.total} tone="bg-blue-50 text-blue-900" testId="stat-deadlines-total" />
        <StatCard icon={AlertTriangle} label="Scadute" value={deadlines.overdue} tone="bg-red-50 text-red-700" testId="stat-deadlines-overdue" />
        <StatCard icon={CalendarClock} label="Urgenti (≤7gg)" value={deadlines.urgent} tone="bg-orange-50 text-orange-700" testId="stat-deadlines-urgent" />
        <StatCard icon={CalendarClock} label="A breve (≤30gg)" value={deadlines.soon} tone="bg-amber-50 text-amber-700" testId="stat-deadlines-soon" />
      </section>

      {consultantKpi && (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-consultant-kpi-grid">
            <StatCard icon={CheckCircle2} label="DVR completi" value={consultantKpi.dvr_complete} tone="bg-emerald-50 text-emerald-700" testId="stat-dvr-complete" />
            <StatCard icon={AlertTriangle} label="DVR incompleti" value={consultantKpi.dvr_incomplete} tone="bg-amber-50 text-amber-700" testId="stat-dvr-incomplete" />
            <StatCard icon={Clock} label="DVR da aggiornare" value={consultantKpi.dvr_to_update} tone="bg-orange-50 text-orange-700" testId="stat-dvr-to-update" />
            <StatCard icon={Flame} label="Rischi alti aperti" value={consultantKpi.high_risks_open} tone="bg-rose-50 text-rose-700" testId="stat-high-risks-open" />
            <StatCard icon={GraduationCap} label="Formazione scaduta" value={consultantKpi.training_expired} tone="bg-red-50 text-red-700" testId="stat-training-expired" />
            <StatCard icon={Heart} label="Visite mediche ≤30gg" value={consultantKpi.health_expiring} tone="bg-pink-50 text-pink-700" testId="stat-health-expiring" />
            <StatCard icon={FileWarning} label="Documenti mancanti" value={consultantKpi.missing_documents} tone="bg-amber-50 text-amber-700" testId="stat-missing-documents" />
            <StatCard icon={ListChecks} label="Azioni miglioramento" value={(consultantKpi.charts?.actions_status || []).reduce((s, x) => s + (x.value || 0), 0)} tone="bg-blue-50 text-blue-900" testId="stat-actions-total" />
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dashboard-charts-section">
            <div className="mb-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Grafici operativi</p>
              <h3 className="mt-1 font-heading text-2xl font-bold text-slate-950">Distribuzione rischi, scadenze e azioni</h3>
              <p className="mt-1 text-sm text-slate-600">Visualizzazione aggregata di tutte le aziende a cui hai accesso.</p>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <div data-testid="chart-card-risks-by-category">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Rischi per categoria</p>
                <RisksByCategoryChart data={consultantKpi.charts?.risks_by_category || []} />
              </div>
              <div data-testid="chart-card-risks-by-level">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Rischi per livello</p>
                <RisksByLevelChart data={consultantKpi.charts?.risks_by_level || []} />
              </div>
              <div data-testid="chart-card-deadlines-by-month">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Scadenze per mese (prossimi 12)</p>
                <DeadlinesByMonthChart data={consultantKpi.charts?.deadlines_by_month || []} />
              </div>
              <div data-testid="chart-card-actions-status">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Stato azioni miglioramento</p>
                <ActionsStatusChart data={consultantKpi.charts?.actions_status || []} />
              </div>
            </div>
          </section>
        </>
      )}

      {kpi && (
        <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dashboard-kpi-section">
          <div className="mb-5 flex items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">{t("dashboard.advanced_kpi")}</p>
              <h3 className="mt-1 font-heading text-2xl font-bold text-slate-950">KPI multi-azienda</h3>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700" data-testid="dashboard-kpi-signed">
              <ShieldCheck className="h-4 w-4" /> {kpi.signed_dvr_total} {t("dashboard.signed_dvr")}
            </div>
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <KpiBars title={t("dashboard.completion_dist")} testId="kpi-completion" data={Object.entries(kpi.completion_buckets || {})} colors={["bg-rose-400", "bg-amber-400", "bg-blue-400", "bg-emerald-500"]} />
            <KpiBars title={t("dashboard.risk_dist")} testId="kpi-risk" data={Object.entries(kpi.risk_levels || {})} colors={["bg-emerald-400", "bg-amber-400", "bg-orange-500", "bg-rose-500"]} />
            <KpiBars title={t("dashboard.actions_dist")} testId="kpi-actions" data={Object.entries(kpi.actions_status || {})} colors={["bg-slate-400", "bg-blue-400", "bg-emerald-500", "bg-slate-300", "bg-rose-500"]} />
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <KpiTopList title={t("dashboard.top_completion")} testId="kpi-top-completion" items={kpi.top_by_completion} field="completion" suffix="%" />
            <KpiTopList title={t("dashboard.top_overdue")} testId="kpi-top-overdue" items={kpi.top_by_overdue} field="overdue" tone="rose" />
          </div>
        </section>
      )}

      <section className="grid gap-6 xl:grid-cols-[1.4fr_0.8fr]" data-testid="dashboard-content-grid">
        <Card className="rounded-lg border-slate-200 shadow-none" data-testid="dashboard-companies-card">
          <CardHeader><CardTitle className="font-heading text-xl" data-testid="dashboard-companies-title">Aziende recenti</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {stats.companies.length === 0 && <p className="rounded-md bg-slate-50 p-4 text-sm text-slate-600" data-testid="dashboard-empty-companies">Nessuna azienda presente. Crea la prima scheda cliente.</p>}
            {stats.companies.map((company) => <Link key={company.id} to={`/aziende/${company.id}`} className="block rounded-md border border-slate-200 p-4 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-slate-50" data-testid={`dashboard-company-${company.id}`}><div className="flex items-center justify-between gap-3"><div><p className="font-semibold text-slate-950" data-testid={`dashboard-company-name-${company.id}`}>{company.business_name}</p><p className="text-sm text-slate-500" data-testid={`dashboard-company-meta-${company.id}`}>{company.sites_count} sedi · {company.safety_roles_count} ruoli sicurezza · {company.workers_count} lavoratori</p></div><Badge className={company.completion_percentage >= 75 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"} data-testid={`dashboard-company-completion-${company.id}`}>{company.completion_percentage}%</Badge></div><Progress value={company.completion_percentage} className="mt-3 h-2" data-testid={`dashboard-company-progress-${company.id}`} /></Link>)}
          </CardContent>
        </Card>
        <Card className="rounded-lg border-slate-200 shadow-none" data-testid="dashboard-deadlines-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="font-heading text-xl" data-testid="dashboard-deadlines-title">Prossime scadenze</CardTitle>
              <Button asChild variant="ghost" size="sm" data-testid="dashboard-deadlines-link"><Link to="/scadenze">Vedi tutte <ArrowRight className="h-3 w-3" /></Link></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {topDeadlines.length === 0 && <p className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-700" data-testid="dashboard-no-deadlines">Nessuna scadenza in arrivo. Compliance allineata.</p>}
            {topDeadlines.map((item) => (
              <div key={item.id} className="rounded-md border border-slate-200 p-3" data-testid={`dashboard-deadline-${item.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-slate-950 text-sm" data-testid={`dashboard-deadline-title-${item.id}`}>{item.title}</p>
                  <Badge className={`border ${severityClass[item.severity] || "bg-slate-50 text-slate-700"}`} data-testid={`dashboard-deadline-severity-${item.id}`}>{severityLabel[item.severity] || item.severity}</Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500" data-testid={`dashboard-deadline-meta-${item.id}`}>{item.company_name} · {item.due_date} · {item.is_overdue ? `Scaduta da ${Math.abs(item.days_remaining)}gg` : `Tra ${item.days_remaining}gg`}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}